import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { FormikValues, useFormikContext } from "formik";
import { FC, useState } from "react";
import { toast } from "react-toastify";
import { alpha } from "@mui/material/styles";

import Button from "../../common/Button";
import Chip from "../../common/Chip/Chips";
import PatientDialog from "./patientDialog/PatientDialog";
import FilterPopup from "./patientList/FilterPopup";
import PatientTableActionIcons from "./PatientTableActionIcons";

import patientService from "../../../services/patient";

import { Typography } from "@mui/material";
import { useAppDispatch } from "../../../redux/store";
import { setLoadingAction } from "../../../redux/utils/actions";
import { IPaginationGridData } from "../../../types/common/pagination";
import { IPatientTableHeadCell } from "../../../types/patient/patientTable";
import { logger } from "../../../utils/logger";
import { PAGE_SIZE } from "../../../utils/pageination";
import Time from "../../common/Time/Time";
import DownloadPopup from "./patientList/DownloadPopup";
import { buttonStyle, patientTableBtnStyle } from "./styles";
import styles from "./styles.module.scss";
import { patientTableHeadCells } from "./utils";

interface IProps {
  searchKeyword: string;
  gridData: IPaginationGridData;
  setGridData: (data: any) => void;
  pageSize: number;
  pageNo: number;
  setPageNo: (pageNo: any) => void;
}

const PatientListTable: FC<IProps> = ({
  searchKeyword,
  gridData,
  setGridData,
  pageSize,
  pageNo,
  setPageNo,
}) => {
  const [open, setOpen] = useState(false);
  const [patient, setPatient] = useState("");

  const dispatch = useAppDispatch();
  const { values } = useFormikContext<FormikValues>();

  const getPatientTableDataWithoutFilter = async () => {
    try {
      dispatch(setLoadingAction(true));
      const paginationData = await patientService.search(0, PAGE_SIZE, {
        keyword: searchKeyword,
      });
      const modifiedData = await paginationData?.data?.data?.map(
        (item: any) => ({
          ...item,
          isChecked: false,
        })
      );
      dispatch(setLoadingAction(false));
      setGridData({ data: modifiedData, result: paginationData?.data?.result });
    } catch (e) {
      dispatch(setLoadingAction(false));
      logger.error(e);
    }
  };

  const getPatientTableData = async (
    newPageNo: number,
    newPageSize: number
  ) => {
    try {
      // for filtration
      let createdByInfo = [];
      let approvedByInfo = [];
      for (let i = 0; i < values.createdBy.length; i++) {
        if (values.createdBy[i].isChecked) {
          createdByInfo.push(values.createdBy[i]._id);
        }
      }
      for (let i = 0; i < values.approvedBy.length; i++) {
        if (values.approvedBy[i].isChecked) {
          approvedByInfo.push(values.approvedBy[i]._id);
        }
      }
      const filtrationInfo = {
        status: [...values.status],
        district: values.district,
        createdBy: createdByInfo,
        approvedBy: approvedByInfo,
        startDate: values.fromDate,
        endDate: values.toDate,
        keyword: searchKeyword,
      };

      dispatch(setLoadingAction(true));
      const paginationData = await patientService.search(
        newPageNo,
        newPageSize,
        filtrationInfo
      );
      const modifiedData = await paginationData?.data?.data?.map(
        (item: any) => ({
          ...item,
          isChecked: false,
        })
      );
      dispatch(setLoadingAction(false));
      setGridData({ data: modifiedData, result: paginationData?.data?.result });
    } catch (e) {
      dispatch(setLoadingAction(false));
      logger.error(e);
    }
  };

  const getPrevPage = async () => {
    await getPatientTableData(pageNo - 1, pageSize);
    setPageNo((prev: any) => prev - 1);
  };

  const getNextPage = async () => {
    await getPatientTableData(pageNo + 1, pageSize);
    setPageNo((prev: any) => prev + 1);
  };

  const viewPatient = (id: string) => {
    setPatient(id);
    setOpen(true);
  };

  const deletePatient = async (id: string) => {
    try {
      dispatch(setLoadingAction(true));
      const res = await patientService.delete(id);
      await getPaginationData();
      dispatch(setLoadingAction(false));
      toast.success(res.data.msg);
    } catch (error: any) {
      dispatch(setLoadingAction(false));
      toast.error(error.response.data.msg);
    }
  };

  const getPaginationData = async () => {
    await getPatientTableData(pageNo, PAGE_SIZE);
  };

  const isValidCanUpdatePatient = (item: any) => item.canUpdate;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Box className={styles.tableHeader}>
          <p></p>
          <Box sx={{ display: "flex", gap: "50px" }}>
            <DownloadPopup />
            <FilterPopup
              setPageNo={setPageNo}
              getPatientTableData={getPatientTableData}
              getPatientTableDataWithoutFilter={
                getPatientTableDataWithoutFilter
              }
            />
          </Box>
        </Box>

        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <TableHead>
              <TableRow>
                {patientTableHeadCells.map(
                  (headCell: IPatientTableHeadCell) => (
                    <TableCell
                      className={styles.thead}
                      key={headCell.id}
                      align={headCell.align}
                    >
                      {/* <TableSortLabel sx={buttonStyle}> */}
                      <Typography variant="h6" sx={buttonStyle}>
                        {" "}
                        {headCell.label}
                      </Typography>
                      {/* </TableSortLabel> */}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {gridData?.data?.length > 0 &&
                gridData?.data?.map((item, index) => {
                  const canUpdate = isValidCanUpdatePatient(item);
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={item._id}
                      sx={{
                        ...(canUpdate && {
                          backgroundColor: alpha("#4caf50", 0.1),
                          "&:hover": {
                            backgroundColor: alpha("#4caf50", 0.15),
                          },
                          borderLeft: "4px solid #4caf50",
                          transition: "all 0.2s ease-in-out",
                        }),
                        ...(index !== 0 && {
                          "&:hover": {
                            backgroundColor: alpha("#000", 0.04),
                          },
                        }),
                      }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          ...(canUpdate && {
                            fontWeight: "bold",
                          }),
                        }}
                        className={styles.tableDefine}
                      >
                        <span
                          style={{
                            color: canUpdate ? "#2e7d32" : "#da3923",
                            whiteSpace: "nowrap",
                            fontWeight: canUpdate ? 600 : "normal",
                          }}
                        >
                          {item.ID?.toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell className={styles.tableDefine} align="left">
                        {item.name}
                      </TableCell>
                      <TableCell className={styles.tableDefine} align="left">
                        {item.phone}
                      </TableCell>
                      <TableCell className={styles.tableDefine} align="left">
                        {item.createdBy?.name}
                      </TableCell>
                      <TableCell className={styles.tableDefine} align="left">
                        <Time time={item.submittedAt} format="MMM DD, YYYY" />
                      </TableCell>
                      <TableCell className={styles.tableDefine} align="left">
                        <Time time={item.updatedAt} format="MMM DD, YYYY" />
                      </TableCell>
                      <TableCell className={styles.tableDefine} align="left">
                        {item.lastActionBy.name}
                      </TableCell>
                      <TableCell className={styles.tableDefine} align="center">
                        {<Chip label={item.status} color="warning" />}
                      </TableCell>

                      <TableCell className={styles.tableDefine} align="right">
                        <PatientTableActionIcons
                          item={item}
                          handleView={viewPatient}
                          handleDelete={deletePatient}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <Paper elevation={0} sx={patientTableBtnStyle}>
          <Button
            type="button"
            disabled={pageNo === 0}
            sx={buttonStyle}
            onClick={getPrevPage}
          >
            Previous
          </Button>
          <span>
            {gridData?.data?.length > 0 ? pageNo * pageSize + 1 : 0}-
            {gridData?.data?.length > 0
              ? pageNo * pageSize + gridData?.data?.length
              : 0}{" "}
            of {gridData?.data?.length > 0 ? gridData?.result : 0}
          </span>
          <Button
            type="button"
            disabled={(pageNo + 1) * pageSize >= gridData?.result}
            sx={buttonStyle}
            onClick={getNextPage}
          >
            Next
          </Button>
        </Paper>
      </Paper>
      {open && (
        <PatientDialog
          open={open}
          setOpen={setOpen}
          id={patient}
          callback={getPaginationData}
        />
      )}
    </Box>
  );
};

export default PatientListTable;
